import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { Plus, Trash2, ChevronUp, ChevronDown, Settings, Save, Eye, Copy, CircleDot as DragHandleDots2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface FormField {
  id: string;
  name: string;
  label: string;
  type: string;
  required: boolean;
  validation: any;
  options: any[];
  step: number;
  order_position: number;
  score_weight: number;
  settings: any;
}

interface SortableFieldProps {
  field: FormField;
  onEdit: (field: FormField) => void;
  onDelete: (id: string) => void;
  onDuplicate: (field: FormField) => void;
}

const fieldTypes = [
  { value: 'text', label: 'Text Input' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Phone' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'checkbox', label: 'Checkboxes' }
];

const SortableField: React.FC<SortableFieldProps> = ({ field, onEdit, onDelete, onDuplicate }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white border rounded-lg p-4 mb-4 hover:border-primary transition-colors"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            type="button"
            className="cursor-move text-gray-400 hover:text-gray-600"
            {...attributes}
            {...listeners}
          >
            <DragHandleDots2 className="h-5 w-5" />
          </button>
          <span className="ml-3 font-medium">{field.label}</span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => onEdit(field)}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="Edit field"
          >
            <Settings className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onDuplicate(field)}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="Duplicate field"
          >
            <Copy className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(field.id)}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="Delete field"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="mt-2 text-sm text-gray-500">
        Type: {fieldTypes.find(t => t.value === field.type)?.label}
        {field.required && <span className="ml-2 text-red-500">*Required</span>}
      </div>
    </div>
  );
};

const LeadFormBuilder: React.FC = () => {
  const [fields, setFields] = useState<FormField[]>([]);
  const [editingField, setEditingField] = useState<FormField | null>(null);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const queryClient = useQueryClient();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Save form mutation
  const saveForm = useMutation({
    mutationFn: async () => {
      // First save the form
      const { data: form, error: formError } = await supabase
        .from('lead_forms')
        .insert({
          name: 'New Lead Form',
          status: 'draft'
        })
        .select()
        .single();

      if (formError) throw formError;

      // Then save all fields
      const { error: fieldsError } = await supabase
        .from('lead_form_fields')
        .insert(
          fields.map((field, index) => ({
            ...field,
            form_id: form.id,
            order_position: index
          }))
        );

      if (fieldsError) throw fieldsError;

      return form;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadForms'] });
      toast.success('Form saved successfully');
    },
    onError: (error) => {
      toast.error('Failed to save form');
      console.error('Save error:', error);
    }
  });

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setFields((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const addField = (type: string) => {
    const newField: FormField = {
      id: crypto.randomUUID(),
      name: `field_${fields.length + 1}`,
      label: 'New Field',
      type,
      required: false,
      validation: {},
      options: [],
      step: 1,
      order_position: fields.length,
      score_weight: 0,
      settings: {}
    };

    setFields([...fields, newField]);
    setEditingField(newField);
    setShowFieldModal(true);
  };

  const updateField = (field: FormField) => {
    setFields(fields.map(f => f.id === field.id ? field : f));
    setEditingField(null);
    setShowFieldModal(false);
  };

  const deleteField = (id: string) => {
    setFields(fields.filter(f => f.id !== id));
  };

  const duplicateField = (field: FormField) => {
    const newField = {
      ...field,
      id: crypto.randomUUID(),
      name: `${field.name}_copy`,
      label: `${field.label} (Copy)`,
      order_position: fields.length
    };
    setFields([...fields, newField]);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-2xl font-bold">Form Builder</h2>
          <p className="text-gray-600">Create high-converting lead capture forms</p>
        </div>
      </div>

      {/* Field Type Selection */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <h3 className="text-lg font-medium mb-4">Add Field</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {fieldTypes.map((type) => (
            <button
              key={type.value}
              onClick={() => addField(type.value)}
              className="flex flex-col items-center p-4 border rounded-lg hover:border-primary hover:bg-primary/5 transition-colors"
            >
              <Plus className="h-6 w-6 mb-2" />
              <span className="text-sm">{type.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Form Fields */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium mb-4">Form Fields</h3>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={fields.map(f => f.id)}
            strategy={verticalListSortingStrategy}
          >
            {fields.map((field) => (
              <SortableField
                key={field.id}
                field={field}
                onEdit={setEditingField}
                onDelete={deleteField}
                onDuplicate={duplicateField}
              />
            ))}
          </SortableContext>
        </DndContext>

        {fields.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No fields added yet. Add your first field above.
          </div>
        )}
      </div>

      {/* Field Edit Modal */}
      {showFieldModal && editingField && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-medium mb-4">Edit Field</h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateField(editingField);
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700">Label</label>
                <input
                  type="text"
                  value={editingField.label}
                  onChange={(e) => setEditingField({ ...editingField, label: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={editingField.name}
                  onChange={(e) => setEditingField({ ...editingField, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingField.required}
                    onChange={(e) => setEditingField({ ...editingField, required: e.target.checked })}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Required field</span>
                </label>
              </div>

              {(editingField.type === 'select' || editingField.type === 'radio') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Options</label>
                  <div className="space-y-2">
                    {editingField.options.map((option, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={option.label}
                          onChange={(e) => {
                            const newOptions = [...editingField.options];
                            newOptions[index] = {
                              ...newOptions[index],
                              label: e.target.value,
                              value: e.target.value.toLowerCase().replace(/\s+/g, '_')
                            };
                            setEditingField({ ...editingField, options: newOptions });
                          }}
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newOptions = editingField.options.filter((_, i) => i !== index);
                            setEditingField({ ...editingField, options: newOptions });
                          }}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const newOptions = [...editingField.options, { label: '', value: '' }];
                        setEditingField({ ...editingField, options: newOptions });
                      }}
                      className="text-sm text-primary hover:text-primary-dark"
                    >
                      Add Option
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Score Weight</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={editingField.score_weight}
                  onChange={(e) => setEditingField({ ...editingField, score_weight: parseInt(e.target.value) })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Higher weight means this field contributes more to lead scoring
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setEditingField(null);
                    setShowFieldModal(false);
                  }}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadFormBuilder;